import { Box, Button } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import HospitalSelectorDropdown from "./HospitalSelectorDropdown";
import { useNavigate } from "react-router";
import useGetHospitalsListQuery from "../../hooks/useGetHospitalsListQuery";
import useAuthValue from "../../../authModule/hooks/useAuthValue";
import useSelectedHospitalValue, {
  setSelectedHospitalValue,
} from "../../hooks/useSelectedHospitalValue";
import getInitials from "../../../../helpers/getInitials";
import { globalStyles } from "../../../../styles/GlobalStyles";
import useGetTenantLogoQuery from "../../../tenantModule/hooks/useGetTenantLogoQuery";

const hospitalColors = ["#000000", "#BE3C3C", "#75C1AF", "#CA5BBD"];

const HospitalSelector = () => {
  const navigate = useNavigate();
  const { tenantId, hospitalId } = useAuthValue();
  const selectedHospitalInfo = useSelectedHospitalValue();

  const { data: tenantLogo } = useGetTenantLogoQuery(
    selectedHospitalInfo?.tenantId
  );

  const [dropdownAnchorEl, setDropdownAnchorEl] = useState<null | HTMLElement>(
    null
  );

  const { data: hospitalsListData = [] } = useGetHospitalsListQuery(tenantId);

  // Set initial hospital region when component mounts
  useEffect(() => {
    if (hospitalId) {
      const userDefaultHospital = hospitalsListData.find(
        (h) => h.uuid === hospitalId
      );
      if (userDefaultHospital) {
        setSelectedHospitalValue(userDefaultHospital);
      }
    }
  }, [hospitalId, hospitalsListData]);

  const hospitalOptions = useMemo(() => {
    if (!hospitalsListData) return [];

    return [...hospitalsListData]
      .sort((a, b) => {
        if (a.uuid === hospitalId) return -1;
        if (b.uuid === hospitalId) return 1;
        return 0;
      })
      .map((h, index) => ({
        key: h.uuid,
        value: h.name,
        color: hospitalColors[index],
      }));
  }, [hospitalsListData, hospitalId]);

  const handlePress = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (dropdownAnchorEl) {
      setDropdownAnchorEl(null);
    } else {
      setDropdownAnchorEl(e.currentTarget);
    }
  };

  return (
    <>
      <Box display="flex" alignItems="center" gap={1}>
        {tenantLogo?.url && (
          <Box
            bgcolor="custom.background.primary"
            height={40}
            width="auto"
            px={0.5}
            borderRadius={10}
            boxSizing="border-box"
            overflow="hidden"
            sx={[globalStyles.center]}
          >
            <img
              src={tenantLogo?.url}
              alt="Logo"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
        )}
        <Button
          onClick={handlePress}
          sx={{
            bgcolor: "custom.background.primary",
            height: 40,
            width: "auto",
            p: 0.5,
            gap: 2,
            borderRadius: "999px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          {selectedHospitalInfo && (
            <Box
              width={33}
              height={33}
              borderRadius={33}
              sx={[
                globalStyles.center,
                {
                  bgcolor: "#5921F2",
                  color: "#FFFFFF",
                  fontSize: 12,
                  fontWeight: 700,
                },
              ]}
            >
              {getInitials(selectedHospitalInfo?.name)}
            </Box>
          )}
          <Box
            component="span"
            width={0}
            height={0}
            mr={1}
            sx={{
              borderLeft: "5px solid transparent",
              borderRight: "5px solid transparent",
              borderTop: "5px solid #000000",
            }}
          />
        </Button>
      </Box>

      <HospitalSelectorDropdown
        anchorEl={dropdownAnchorEl}
        options={hospitalOptions}
        onChange={(selected) => {
          // Find the selected hospital to get its region
          const selectedHospitalData =
            hospitalsListData.find((h) => h.uuid === selected.key) ?? {};
          setSelectedHospitalValue(selectedHospitalData);
          setDropdownAnchorEl(null);
          navigate("/");
        }}
        onClose={() => setDropdownAnchorEl(null)}
      />
    </>
  );
};

export default HospitalSelector;
